import { defineComponent } from "vue";
import { Vue3Marquee } from "vue3-marquee";

import { PAGE_NAME } from "./attributes";
import { currencyPairs, CurrencyPair } from "@/data/wallet/pairs";

import {
  WalletMain,
  WalletHistory,
  WalletApplications,
  WalletDeposit,
  WalletWithdrawal,
  WalletRequisitesInfo,
  WalletVerification,
  WalletVerificationRequisites,
  WalletCriptoDepositAddress,
  WalletCriptoWithdrawal,
  WalletVerificationWithdrawal,
} from "@/components/pages/wallet";
import {
  SharedBlockCurrencySelect,
  SharedBlockCurrencyPair,
} from "@/components/shared";
import { AdminApplicationType } from "@/shared/repository/modules/admin/application/enums";
import { DepositWithdrawalHistoryShortResponse } from "@/shared/repository/modules/deposit-withdrawal/repo";
import { ApplicationHistoryShortResponse } from "@/shared/repository/modules/application/repo";

export type ModalComponent =
  | typeof WalletDeposit
  | typeof WalletWithdrawal
  | typeof WalletRequisitesInfo
  | typeof WalletVerification
  | typeof WalletVerificationRequisites
  | typeof WalletCriptoDepositAddress
  | typeof WalletCriptoWithdrawal
  | typeof WalletVerificationWithdrawal
  | typeof WalletMain
  | "";

export type TickerPair = {
  currency_id_from: number;
  currency_id_to: number;
  currency_type_from: "fiat" | "crypto";
  currency_type_to: "fiat" | "crypto";
};

const isSamePair = (pair1: TickerPair, pair2: TickerPair): boolean =>
  pair1.currency_id_from === pair2.currency_id_from &&
  pair1.currency_id_to === pair2.currency_id_to;

const addPairIfMissing = (pairs: TickerPair[], pairToAdd: TickerPair): void => {
  if (!pairs.some((pair) => isSamePair(pair, pairToAdd))) {
    pairs.push(pairToAdd);
  }
};

export default defineComponent({
  name: PAGE_NAME,
  components: {
    WalletMain,
    WalletHistory,
    WalletApplications,
    WalletDeposit,
    WalletWithdrawal,
    WalletRequisitesInfo,
    WalletVerification,
    WalletCriptoDepositAddress,
    WalletCriptoWithdrawal,
    WalletVerificationWithdrawal,
    SharedBlockCurrencySelect,
    SharedBlockCurrencyPair,
    Vue3Marquee,
  },
  data() {
    return {
      depositWithdrawalHistory: <DepositWithdrawalHistoryShortResponse[]>[],
      applicationHistory: <ApplicationHistoryShortResponse[]>[],
      walletRepo: this.$projectServices.walletRepo,
      depositWithdrawalRepo: this.$projectServices.depositWithdrawalRepo,
      depositWithdrawalRepository:
        this.$projectServices.depositWithdrawalRepository,
      applicationRepository: this.$projectServices.applicationRepository,
      walletRepository: this.$projectServices.walletRepository,

      pairs: currencyPairs,
      ratesMap: {} as Record<string, { buy: number; sell: number }>,
      shouldAnimate: false,
      tickerInterval: null as ReturnType<typeof setInterval> | null,
      resizeObserver: null as ResizeObserver | null,
      isInitialTickerLoad: true,
      selectedPair: null as TickerPair | null,
    };
  },
  async created(): Promise<void> {
    await this.loadData();
  },
  async mounted(): Promise<void> {
    this.setupResizeObserver();
    window.addEventListener("resize", () => this.checkIfAnimationNeeded());

    this.tickerInterval = setInterval(() => {
      this.loadTicker();
    }, 60000);
  },
  beforeUnmount(): void {
    this.depositWithdrawalRepo.UPDATE_DEPOSIT({
      id: 0,
      currency_id: 0,
      amount: 0,
      currencyName: "",
    });
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.tickerInterval) {
      clearInterval(this.tickerInterval);
    }
  },
  computed: {
    currencyMap(): Record<number, string> {
      const map: Record<number, string> = {};
      this.walletRepo.list.forEach((currency) => {
        map[currency.id] = currency.name;
      });
      return map;
    },

    pairsWithRates(): Array<
      CurrencyPair & { buyRate: number; sellRate: number }
    > {
      return this.pairs.map((pair: CurrencyPair) => {
        const key = `${pair.currency_id_from}-${pair.currency_id_to}`;
        const rates = this.ratesMap[key] || { buy: 0, sell: 0 };
        return {
          ...pair,
          buyRate: rates.buy,
          sellRate: rates.sell,
        };
      });
    },

    selectedPairBuyRate(): number {
      if (!this.selectedPair) return 0;

      const key = `${this.selectedPair.currency_id_from}-${this.selectedPair.currency_id_to}`;
      return this.ratesMap[key]?.buy || 0;
    },

    selectedPairSellRate(): number {
      if (!this.selectedPair) return 0;
      const key = `${this.selectedPair.currency_id_from}-${this.selectedPair.currency_id_to}`;
      return this.ratesMap[key]?.sell || 0;
    },

    displayedModal(): ModalComponent {
      switch (this.walletRepo.modal.type) {
        case AdminApplicationType.deposit:
          return WalletDeposit;
        case AdminApplicationType.withdrawal:
          return WalletWithdrawal;
        case AdminApplicationType.depositRequisites:
          return WalletRequisitesInfo;
        case AdminApplicationType.verificationDeposit:
        case AdminApplicationType.verificationWithdrawal:
          return WalletVerification;
        case AdminApplicationType.verificationWithdrawalRequisites:
          return WalletVerificationRequisites;
        case AdminApplicationType.criptoDepositAddress:
          return WalletCriptoDepositAddress;
        case AdminApplicationType.walletWithdrawal:
          return WalletCriptoWithdrawal;
        case AdminApplicationType.verificationWalletWithdrawal:
          return WalletVerificationWithdrawal;
        case AdminApplicationType.verificationBankRequisites:
          return WalletVerificationRequisites;
        default:
          return "";
      }
    },

    isModalShow(): boolean {
      return !!this.walletRepo.modal.type;
    },
  },
  methods: {
    async loadData() {
      try {
        const [depositWithdrawalHistory, applicationHistory] =
          await Promise.all([
            this.depositWithdrawalRepository.historyShort(),
            this.applicationRepository.historyShort(),
          ]);
        this.depositWithdrawalHistory = depositWithdrawalHistory;
        this.applicationHistory = applicationHistory;
      } catch (error) {
        console.log(error);
      }
    },

    closeModal() {
      this.walletRepo.modal.type = "";
    },

    async loadTicker(singlePair?: TickerPair) {
      try {
        const pairsToLoad: TickerPair[] = singlePair
          ? [singlePair]
          : this.pairs.map((pair) => ({
              currency_id_from: pair.currency_id_from,
              currency_id_to: pair.currency_id_to,
              currency_type_from: pair.currency_type_from,
              currency_type_to: pair.currency_type_to,
            }));

        if (this.selectedPair) {
          addPairIfMissing(pairsToLoad, this.selectedPair);
        }

        const res = await this.walletRepository.ticker({ pairs: pairsToLoad });

        const ratesMap: Record<string, { buy: number; sell: number }> =
          singlePair ? { ...this.ratesMap } : {};

        res.quotes.forEach((quote) => {
          const key = `${quote.pair.currency_id_from}-${quote.pair.currency_id_to}`;
          ratesMap[key] = {
            buy: quote.buy_price,
            sell: quote.sell_price,
          };
        });

        this.ratesMap = ratesMap;
        this.checkIfAnimationNeeded();
      } catch (error) {
        console.error("Failed to load ticker:", error);
      }
    },

    handlePairChanged(pair: TickerPair) {
      this.selectedPair = pair;

      if (this.isInitialTickerLoad) {
        // При первой загрузке загружаем все пары
        this.isInitialTickerLoad = false;
        this.loadTicker();
      } else {
        // При последующих изменениях загружаем только выбранную пару
        this.loadTicker(pair);
      }
    },

    setupResizeObserver() {
      this.$nextTick(() => {
        const pairsContainer = this.$el.querySelector(
          ".wallet-page__pairs"
        ) as HTMLElement;
        const marqueeContent = this.$el.querySelector(
          ".vue3-marquee .marquee"
        ) as HTMLElement;

        if (pairsContainer && marqueeContent) {
          this.resizeObserver = new ResizeObserver(() => {
            this.checkIfAnimationNeeded();
          });

          this.resizeObserver.observe(pairsContainer);
          this.resizeObserver.observe(marqueeContent);

          this.checkIfAnimationNeeded();
        }
      });
    },

    checkIfAnimationNeeded() {
      const pairsContainer = this.$el.querySelector(
        ".wallet-page__pairs"
      ) as HTMLElement;
      const marqueeContent = this.$el.querySelector(
        ".vue3-marquee .marquee"
      ) as HTMLElement;

      if (pairsContainer && marqueeContent) {
        const containerWidth = pairsContainer.offsetWidth;
        const contentWidth = marqueeContent.scrollWidth;

        this.shouldAnimate = contentWidth > containerWidth;
      }
    },
  },
});
